import {
    CHANGE_DAY_COLUMN_PREFIX,
    EColumnsFields,
    FIRST_DAY_COLUMN_PREFIX,
    LAST_DAY_COLUMN_PREFIX,
} from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import { isUnifiedPositionEnabled } from "app/components/campaigns/shared/utils";
import { EKeywordsReportKeywordsTableMetrics } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import type { IKWsReportSortTableParams } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/TableUtils/RankTrackerTablesTypes";

const tableTabsMap = {
    [EKeywordsReportKeywordsTableMetrics.POSITION]: "top_pos",
    [EKeywordsReportKeywordsTableMetrics.UNIVERSAL_POSITION]: "top_overall_pos",
    [EKeywordsReportKeywordsTableMetrics.CLICKS]: "clicks",
};

const DEFAULT_COLUMN_TO_SORT = "volume";

// The "Updated" column's dataIndex is the formatted display string (EColumnsFields.LAST_UPDATED).
// The backend only recognizes the raw "lastUpdated" sort token, so it must be translated here.
const LAST_UPDATED_SORT_FIELD = "lastUpdated";

const normalizeColumnToSort = (columnToSort: string): string => {
    if (!isUnifiedPositionEnabled()) {
        return columnToSort;
    }
    return columnToSort.replaceAll(
        EKeywordsReportKeywordsTableMetrics.UNIVERSAL_POSITION,
        EKeywordsReportKeywordsTableMetrics.POSITION,
    );
};

const getPrefixToSplit = (metricToSplit: string, columnToSort: string): string => {
    switch (true) {
        case columnToSort.includes(`${metricToSplit}${EKeywordsReportKeywordsTableMetrics.POSITION}`):
            return `${metricToSplit}${EKeywordsReportKeywordsTableMetrics.POSITION}`;
        case columnToSort.includes(`${metricToSplit}${EKeywordsReportKeywordsTableMetrics.UNIVERSAL_POSITION}`):
            return `${metricToSplit}${EKeywordsReportKeywordsTableMetrics.UNIVERSAL_POSITION}`;
        default:
            return `${metricToSplit}${EKeywordsReportKeywordsTableMetrics.CLICKS}`;
    }
};

const sortGorupedColumns = (columnToSort: string, metricToSplit: string) => {
    const { POSITION, CLICKS, UNIVERSAL_POSITION } = EKeywordsReportKeywordsTableMetrics;

    switch (true) {
        case columnToSort.includes(`${metricToSplit}${POSITION}`):
            return tableTabsMap[POSITION];
        case columnToSort.includes(`${metricToSplit}${CLICKS}`):
            return tableTabsMap[CLICKS];
        default:
            return isUnifiedPositionEnabled() ? tableTabsMap[POSITION] : tableTabsMap[UNIVERSAL_POSITION];
    }
};

export const getKeywordsReportSortColumn = (
    columnToSort = DEFAULT_COLUMN_TO_SORT,
    asc = true,
): IKWsReportSortTableParams => {
    const normalizedColumnToSort = normalizeColumnToSort(columnToSort);
    const sort: IKWsReportSortTableParams = {
        sort: normalizedColumnToSort,
        asc,
    };
    if (!normalizedColumnToSort) {
        return sort;
    }
    if (normalizedColumnToSort === (EColumnsFields.LAST_UPDATED as string)) {
        return { sort: LAST_UPDATED_SORT_FIELD, asc };
    }
    const isFirstDayColumn = normalizedColumnToSort.includes(FIRST_DAY_COLUMN_PREFIX);
    const isLastDayColumn = normalizedColumnToSort.includes(LAST_DAY_COLUMN_PREFIX);
    if (normalizedColumnToSort.includes(CHANGE_DAY_COLUMN_PREFIX)) {
        const prefixToSplit = getPrefixToSplit(CHANGE_DAY_COLUMN_PREFIX, normalizedColumnToSort);
        const [_, siteToSort] = normalizedColumnToSort.split(prefixToSplit);
        return {
            sortSite: siteToSort,
            sortMetricType: CHANGE_DAY_COLUMN_PREFIX,
            sort: sortGorupedColumns(normalizedColumnToSort, CHANGE_DAY_COLUMN_PREFIX),
            asc,
        };
    } else if (isFirstDayColumn || isLastDayColumn) {
        const metricToSplit = isFirstDayColumn ? FIRST_DAY_COLUMN_PREFIX : LAST_DAY_COLUMN_PREFIX;
        const prefixToSplit = getPrefixToSplit(metricToSplit, normalizedColumnToSort);
        const [_, siteToSort] = normalizedColumnToSort.split(prefixToSplit);

        return {
            sortSite: siteToSort,
            sortMetricType: isFirstDayColumn ? "First" : "Last",
            sort: sortGorupedColumns(normalizedColumnToSort, metricToSplit),
            asc,
        };
    }

    if (isUnifiedPositionEnabled()) {
        if (normalizedColumnToSort === "top_overall_pos" || normalizedColumnToSort === "overallPosition") {
            return {
                ...sort,
                sort: "top_pos",
            };
        }
    }

    return sort;
};
